import React, { useState, useEffect, useReducer, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  RotateCcw,
  Package,
  AlertTriangle,
  Banknote,
  CreditCard,
  Wallet,
  CheckCircle2,
  Loader2,
} from "lucide-react";

const REFUND_METHODS = [
  { value: "cash", label: "Cash Refund", icon: Banknote },
  { value: "upi", label: "UPI Refund", icon: Wallet },
  { value: "card", label: "Card Refund", icon: CreditCard },
  { value: "store_credit", label: "Store Credit", icon: Wallet },
  { value: "adjustment", label: "No Refund (Adjustment)", icon: CheckCircle2 },
];

// Reducer for managing selection state
const selectionReducer = (state, action) => {
  const { type, idx, value, items } = action;
  switch (type) {
    case 'SELECT_ITEM': {
      const newSelected = {};
      Object.keys(state.selected).forEach(k => { newSelected[k] = state.selected[k]; });
      newSelected[idx] = value;
      
      const newQuantities = {};
      Object.keys(state.quantities).forEach(k => { newQuantities[k] = state.quantities[k]; });
      newQuantities[idx] = value && items?.[idx] ? items[idx].available_quantity : 0;
      
      return { selected: newSelected, quantities: newQuantities };
    }
    case 'SET_QUANTITY': {
      const newQuantities = {};
      Object.keys(state.quantities).forEach(k => { newQuantities[k] = state.quantities[k]; });
      newQuantities[idx] = value;
      
      const newSelected = {};
      Object.keys(state.selected).forEach(k => { newSelected[k] = state.selected[k]; });
      if (value > 0) newSelected[idx] = true;
      
      return { selected: newSelected, quantities: newQuantities };
    }
    case 'SELECT_ALL': {
      const newSelected = {};
      const newQuantities = {};
      items.forEach((item, i) => {
        if (item.available_quantity > 0) {
          newSelected[i] = true;
          newQuantities[i] = item.available_quantity;
        }
      });
      return { selected: newSelected, quantities: newQuantities };
    }
    case 'CLEAR_ALL': {
      const newQuantities = {};
      items.forEach((_, i) => { newQuantities[i] = 0; });
      return { selected: {}, quantities: newQuantities };
    }
    case 'INIT': {
      const newQuantities = {};
      items.forEach((_, i) => { newQuantities[i] = 0; });
      return { selected: {}, quantities: newQuantities };
    }
    case 'RESET':
      return { selected: {}, quantities: {} };
    default:
      return state;
  }
};

export default function SalesReturnDialog({ open, onOpenChange, saleId, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saleData, setSaleData] = useState(null);
  const [selection, dispatch] = useReducer(selectionReducer, { selected: {}, quantities: {} });
  const [refundMethod, setRefundMethod] = useState("cash");
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  
  // STEP 3E: Idempotency key ref - persists across retries until dialog closes
  // Key is generated when user clicks submit, not on every render
  const idempotencyKeyRef = useRef(null);

  // Shortcuts for cleaner code
  const selectedItems = selection.selected;
  const returnQuantities = selection.quantities;

  // Load returnable items when dialog opens
  useEffect(() => {
    if (open && saleId) {
      loadReturnableItems();
    }
    // Intentionally keyed only on [open, saleId]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saleId]);

  // Reset state when dialog closes
  const resetState = () => {
    setSaleData(null);
    dispatch({ type: 'RESET' });
    setRefundMethod("cash");
    setReason("");
    setRemarks("");
    // STEP 3E: Clear idempotency key when dialog closes
    idempotencyKeyRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      // Defer state reset to avoid triggering during render
      const timer = setTimeout(resetState, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const loadReturnableItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sales-returns/sale/${saleId}/returnable`);
      setSaleData(data);
      dispatch({ type: 'INIT', items: data.items });
    } catch (e) {
      toast.error(formatApiError(e));
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (idx, checked) => {
    dispatch({ type: 'SELECT_ITEM', idx, value: checked, items: saleData?.items });
  };

  const handleQuantityChange = (idx, value) => {
    const item = saleData.items[idx];
    const qty = Math.max(0, Math.min(item.available_quantity, parseInt(value) || 0));
    dispatch({ type: 'SET_QUANTITY', idx, value: qty });
  };

  const handleSelectAll = () => {
    dispatch({ type: 'SELECT_ALL', items: saleData?.items || [] });
  };

  const handleClearAll = () => {
    dispatch({ type: 'CLEAR_ALL', items: saleData?.items || [] });
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!saleData) return { quantity: 0, amount: 0 };
    
    let totalQty = 0;
    let totalAmount = 0;
    
    saleData.items.forEach((item, idx) => {
      if (selectedItems[idx] && returnQuantities[idx] > 0) {
        totalQty += returnQuantities[idx];
        totalAmount += returnQuantities[idx] * item.unit_price;
      }
    });
    
    return { quantity: totalQty, amount: totalAmount.toFixed(2) };
  };

  const totals = calculateTotals();

  const handleSubmit = async () => {
    // Validate
    if (!reason.trim()) {
      toast.error("Please enter a reason for the return");
      return;
    }
    
    if (totals.quantity === 0) {
      toast.error("Please select at least one item to return");
      return;
    }

    // Build return items
    const returnItems = [];
    saleData.items.forEach((item, idx) => {
      if (selectedItems[idx] && returnQuantities[idx] > 0) {
        returnItems.push({
          sale_line_index: idx,
          product_id: item.product_id,
          quantity: returnQuantities[idx],
        });
      }
    });

    // STEP 3E: Generate idempotency key ONCE per submit attempt
    // Key persists across retries until dialog closes or submission succeeds
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/sales-returns", {
        sale_id: saleId,
        return_items: returnItems,
        refund_method: refundMethod,
        reason: reason.trim(),
        remarks: remarks.trim(),
        return_idempotency_key: idempotencyKeyRef.current,  // STEP 3E: Include key
      });

      if (data.pending_approval) {
        toast.success(`Return ${data.return_number} created - pending owner approval`, {
          description: data.message,
          duration: 6000,
        });
      } else if (data.idempotent) {
        // STEP 3E: Handle idempotent response (retry of already-processed return)
        toast.success(`Return ${data.return_number} already processed`, {
          description: data.message || "Your previous request was successful",
          duration: 4000,
        });
      } else {
        toast.success(`Return ${data.return_number} created successfully`);
      }
      // STEP 3E: Clear key on success before closing dialog
      idempotencyKeyRef.current = null;
      onSuccess?.(data);
      onOpenChange(false);
    } catch (e) {
      // STEP 3E: On error, key persists for retry
      // Only conflict (409) with different data should clear the key
      if (e.response?.status === 409 && e.response?.data?.detail?.includes("different data")) {
        // Different data sent with same key - generate new key for genuinely new attempt
        idempotencyKeyRef.current = null;
      }
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const hasReturnableItems = saleData?.items?.some(i => i.available_quantity > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-orange-600" />
            Sales Return
          </DialogTitle>
          <DialogDescription>
            Process a return for invoice {saleData?.invoice_number || saleId?.slice(0, 8)}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !hasReturnableItems ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <p className="text-lg font-medium">No Items Available for Return</p>
            <p className="text-sm text-muted-foreground mt-1">
              All items from this sale have already been returned.
            </p>
          </div>
        ) : (
          <>
            {/* Sale Info */}
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-muted-foreground">Shop:</span>{" "}
                  <span className="font-medium">{saleData?.shop_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Date:</span>{" "}
                  <span className="font-medium">{formatDateTime(saleData?.sale_date)}</span>
                </div>
                {saleData?.customer_name && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Customer:</span>{" "}
                    <span className="font-medium">{saleData.customer_name}</span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Total:</span>{" "}
                  <span className="font-medium">₹{saleData?.total?.toFixed(2)}</span>
                </div>
                {saleData?.return_status !== "none" && (
                  <div>
                    <Badge variant={saleData.return_status === "fully_returned" ? "destructive" : "outline"}>
                      {saleData.return_status === "partially_returned" ? "Partial Return" : "Fully Returned"}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {/* Item Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Select Items to Return</Label>
                <div className="space-x-2">
                  <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleClearAll}>
                    Clear
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 border rounded-lg p-2">
                {saleData?.items?.map((item, idx) => (
                  <Card
                    key={idx}
                    className={`p-3 transition-colors ${
                      item.available_quantity === 0 
                        ? "opacity-50 bg-slate-50" 
                        : selectedItems[idx] 
                          ? "border-orange-300 bg-orange-50/30" 
                          : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedItems[idx] || false}
                        onCheckedChange={(checked) => handleSelectItem(idx, checked)}
                        disabled={item.available_quantity === 0}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <Package className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                          <span className="font-medium text-sm break-words">
                            {item.product_name}
                          </span>
                          {item.manual && (
                            <Badge variant="secondary" className="text-[10px] shrink-0">Manual</Badge>
                          )}
                        </div>
                        {/* Show barcodes if available */}
                        {!item.manual && (item.auto_barcode || item.manual_barcode) && (
                          <div className="text-[10px] text-muted-foreground font-mono mt-0.5 ml-6">
                            {item.auto_barcode && <span>Auto: {item.auto_barcode}</span>}
                            {item.auto_barcode && item.manual_barcode && <span className="mx-1">·</span>}
                            {item.manual_barcode && <span>Manual: {item.manual_barcode}</span>}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                          <span>Sold: {item.sold_quantity}</span>
                          {item.returned_quantity > 0 && (
                            <span className="text-orange-600">Returned: {item.returned_quantity}</span>
                          )}
                          <span className="text-green-600">Available: {item.available_quantity}</span>
                          <span>@ ₹{item.unit_price?.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="shrink-0 w-20">
                        <Input
                          type="number"
                          min={0}
                          max={item.available_quantity}
                          value={returnQuantities[idx] || 0}
                          onChange={(e) => handleQuantityChange(idx, e.target.value)}
                          disabled={item.available_quantity === 0}
                          className="h-8 text-center"
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Refund Method */}
            <div className="space-y-2">
              <Label>Refund Method</Label>
              <Select value={refundMethod} onValueChange={setRefundMethod}>
                <SelectTrigger data-testid="return-refund-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REFUND_METHODS.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      <div className="flex items-center gap-2">
                        <method.icon className="w-4 h-4" />
                        {method.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason for Return <span className="text-red-500">*</span></Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Customer changed mind, Damaged product, Wrong item"
                data-testid="return-reason"
              />
            </div>

            {/* Remarks */}
            <div className="space-y-2">
              <Label>Additional Remarks (Optional)</Label>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            {/* Summary */}
            <Card className="p-4 bg-orange-50 border-orange-200">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm text-muted-foreground">Return Summary</div>
                  <div className="text-lg font-bold text-orange-700">
                    {totals.quantity} item(s) · ₹{totals.amount}
                  </div>
                </div>
                <Badge variant="outline" className="text-orange-700 border-orange-300">
                  {REFUND_METHODS.find(m => m.value === refundMethod)?.label}
                </Badge>
              </div>
            </Card>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          {hasReturnableItems && (
            <Button
              onClick={handleSubmit}
              disabled={submitting || totals.quantity === 0 || !reason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="submit-return-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Process Return
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
